import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, BadgeCheck, HeartHandshake, Loader2 } from "lucide-react";
import { PageHero, Reveal, usePageMeta } from "@/components/kit";
import { SERVICES, HERO_BG, PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO, ADDRESS, HOURS_WEEK, HOURS_TIME, AFTER_HOURS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROPERTY_TYPES = ["Church", "School", "Emergency Personnel Facility", "Commercial / Business", "Residential", "Other"];

const inputCls =
  "w-full bg-[#0D0D10] border border-gold/20 rounded-sm px-4 py-3.5 text-sm text-[#F4EFE6] placeholder:text-zinc-600 focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/30 transition-colors";

export default function QuotePage() {
  usePageMeta(
    "Request a Free Quote | Redeemer Solutions LLC",
    "Request a free quote for security camera, access control, fiber optics, Ethernet, or video intercom service and repair in Middle Tennessee. Special rates for churches, schools, and first responder facilities."
  );
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", property_type: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", property_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your request. Please call us at (615) 987-0139.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <PageHero
        testid="quote-hero"
        eyebrow="Request a Quote"
        lines={["Tell Us What's", { t: "Not Working", gold: true }]}
        sub="Whether it's a dead camera, a cut fiber line, or a door that won't latch — we'll follow up, schedule a service visit, and give you a straight answer."
        image={HERO_BG}
        imageAlt="Request a security and low-voltage service quote"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4 space-y-6">
          <Reveal>
            <div className="bg-[#121216] border border-gold/15 rounded-sm p-8">
              <p className="font-mono-label text-xs uppercase tracking-[0.26em] text-gold">Reach Us Directly</p>
              <ul className="mt-6 space-y-5 text-sm text-zinc-300">
                <li>
                  <a href={PHONE_TEL} data-testid="quote-phone-link" className="flex items-center gap-3 hover:text-gold-champagne transition-colors">
                    <Phone size={16} className="text-gold shrink-0" /> {PHONE}
                  </a>
                </li>
                <li>
                  <a href={EMAIL_MAILTO} data-testid="quote-email-link" className="flex items-center gap-3 hover:text-gold-champagne transition-colors break-all">
                    <Mail size={16} className="text-gold shrink-0" /> {EMAIL}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="text-gold mt-0.5 shrink-0" /> {ADDRESS}
                </li>
                <li className="flex items-start gap-3">
                  <Clock size={16} className="text-gold mt-0.5 shrink-0" />
                  <span>{HOURS_WEEK} · {HOURS_TIME}<br /><span className="text-gold-champagne/80">{AFTER_HOURS}</span></span>
                </li>
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="bg-[#0D0D10] border border-gold/20 rounded-sm p-8 space-y-5">
              <div className="flex items-start gap-3">
                <BadgeCheck size={18} className="text-gold mt-0.5 shrink-0" />
                <p className="text-sm text-zinc-300 leading-relaxed">ISNetworld Member Contractor — vetted safety and quality practices.</p>
              </div>
              <div className="flex items-start gap-3">
                <HeartHandshake size={18} className="text-gold mt-0.5 shrink-0" />
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Special rates for churches, schools, and emergency personnel facilities — select your property type below.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <Reveal delay={0.05}>
            <form data-testid="quote-form" onSubmit={submit} className="bg-[#121216] border border-gold/15 rounded-sm p-7 sm:p-10">
              <h2 className="font-display text-2xl font-bold text-[#F4EFE6]">Your Free Quote Starts Here</h2>
              <p className="text-zinc-500 text-sm mt-2">Fields marked * are required. We typically respond the same business day.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
                <div>
                  <label htmlFor="quote-name" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Full Name *</label>
                  <input id="quote-name" data-testid="quote-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Zach Alexander" />
                </div>
                <div>
                  <label htmlFor="quote-email" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Email *</label>
                  <input id="quote-email" data-testid="quote-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
                </div>
                <div>
                  <label htmlFor="quote-phone" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Phone *</label>
                  <input id="quote-phone" data-testid="quote-phone-input" type="tel" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(615) 987-0139" />
                </div>
                <div>
                  <label htmlFor="quote-service" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Service Needed *</label>
                  <select id="quote-service" data-testid="quote-service-select" required value={form.service} onChange={set("service")} className={inputCls}>
                    <option value="" disabled>Select a service</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.name}>{s.name}</option>
                    ))}
                    <option value="Multiple / Not Sure Yet">Multiple / Not Sure Yet</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="quote-property" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Property Type</label>
                  <select id="quote-property" data-testid="quote-property-select" value={form.property_type} onChange={set("property_type")} className={inputCls}>
                    <option value="">Select property type (optional)</option>
                    {PROPERTY_TYPES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="quote-message" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Describe the Issue</label>
                  <textarea id="quote-message" data-testid="quote-message-input" rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder="What's not working, when it started, the equipment brand if you know it — anything helps." />
                </div>
              </div>
              <button
                type="submit"
                data-testid="quote-form-submit-button"
                disabled={sending}
                className="mt-8 inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] font-semibold text-sm tracking-wide px-10 py-4 rounded-sm transition-[transform,box-shadow,opacity] duration-300 hover:scale-[1.02] hover:shadow-[0_0_32px_rgba(212,175,55,0.35)] disabled:opacity-60 disabled:hover:scale-100"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : null}
                {sending ? "Sending..." : "Submit Quote Request"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
